import { Dispatch, useEffect, useState } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Select } from '@material-ui/core';
import { Action } from 'redux';

import { OrderSideEnum } from '../../../../lib/enums';
import { OrderStatusEnum } from '../../../../lib/enums/order-status.enum';
import lang from '../../../../lib/language';
import { Order } from '../../../../lib/models/trading/types';
import { getUnreadMessageCount } from '../../../../lib/store/ams';
import { getBuyingPowerFromOMS as getBuyingPowerFromOMSCall } from '../../../../lib/store/common-actions';
import { RootState } from '../../../../lib/store/index';
import {
  getAccountSummary,
  getAccountTradeHistory,
  getOpenPositions as getPositions,
  setHasOrderUpdate,
} from '../../../../lib/store/reporting';
import { PENDING_ORDERS_REQUEST_TENNSTATUS } from '../../../../lib/store/reporting/constants';
import { AccountTradeHistoryPayloadData, PositionType } from '../../../../lib/store/reporting/types';
import { newOrder as serviceNewOrder, resetRequestStatus } from '../../../../lib/store/trading/index';
import MarketStateCache from '../../../../lib/store-util/MarketStateCache';
import SubscribeGroupCache from '../../../../lib/store-util/SubscribeGroupCache';
import SymbolStatusCache from '../../../../lib/store-util/SymbolStatusCache';
import { isOneOf } from '../../../../lib/util/DataHelpers';
import { checkIfSymbolIsHalted } from '../../../../lib/util/HaltSymbolHelper';
import { isMarketClosed, isMarketRegularHours } from '../../../../lib/util/MarketDataHelpers';
import { orderStatusToCallStatus } from '../../../../lib/util/TradingHelpers';

import ArrowBuyOrSell from '../../../../assets/img/arrowBuyOrSell.png';
import BuyAndSellDot from '../../../../assets/img/BuyAndSellDot.png';
import ModalCreateOrder from '../../../../containers/SymbolDetails/modalCreateOrder/ModalCreateOrder';
import { useIsMobile } from '../../../../hooks';
import Button from '../../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../../../util/routes';
import { CapitalBuySellButtonsConditions, ClickableDiv, ProcessFinishedModal, RegularTradingHoursStartModal } from '../../..';
import HaltedSymbol from '../../../OrderActions/HaltedSymbol/HaltedSymbol';
import {
  getDefaultMessageTitle,
  proccessPartiallyRejectedMessage,
  proccessSuccessMessageBody,
  proccessSuccessMessageTitle,
  processFailMessageManager,
} from '../../../OrderActions/OrderHelpers';
import useNewOrderTimer from '../../../OrderActions/useNewOrderTimer';

import styles from './ButtonsSellAndBuy.module.scss';

type Props = {
  isSwipeable?: boolean;
  row: any;
  showButtons?: boolean | undefined;
  setIsSellOrBuySelected?: (value: boolean) => void;
} & ReduxProps;

const ButtonsSellAndBuy = (props: Props) => {
  const {
    requestStatus,
    row,
    isSwipeable,
    submittedOrder,
    getOpenPositions,
    getOrders,
    myStocksUpdate,
    newOrder,
    resetStatus,
    getAccSummary,
    getUnreadCount,
    showButtons,
    getBuyingPowerFromOMS,
    setIsSellOrBuySelected,
  } = props;
  const { isMobile } = useIsMobile();
  const navigate = useNavigate();
  const [isSell, setIsSell] = useState<boolean>(false);
  const [order, setOrder] = useState<Order>({});
  const [modalTitle, setModalTitle] = useState<string>('');
  const [fromReview, setFromReview] = useState<boolean>(false);
  const [isHalted, setIsHalted] = useState<boolean>(false);
  const [positionsQuantity, setPositionsQuantity] = useState<number>(0);
  const [showFinishModal, setShowFinishModal] = useState<boolean>(false);
  const [notEnoughStocks, setNotEnoughStocks] = useState<boolean>(false);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState<boolean>(false);
  const [visibleRegularTradingHoursStartDialog, setVisibleRegularTradingHoursStartDialog] = useState<boolean>(false);
  const { marketState } = MarketStateCache.use();
  const { name, id, positionType, quantity } = row;
  const symbolsStatusCache = SymbolStatusCache.use(name);

  const { ordType, orderQty, side, status, filledSoFar } = submittedOrder;
  const rejectedOrder = requestStatus === OrderStatusEnum.Rejected;
  const orderInProgress = requestStatus === OrderStatusEnum.InProgress;
  const partiallyRejectedOrder = status === OrderStatusEnum.PartiallyReject;

  const {
    orderTimeoutPassed, showExpired, showFillOrder, hasNoResponse,
  } = useNewOrderTimer(showFinishModal, ordType, status, requestStatus);

  const marketClosed = isMarketClosed(marketState);

  useEffect(() => {
    if (fromReview && requestStatus !== OrderStatusEnum.Pending && !showFinishModal) {
      if (
        !marketClosed
        && ((!isSell && positionType === PositionType.Sell
          && order.orderQty && Math.abs(quantity) < order.orderQty)
        || (isSell && positionType === PositionType.Buy && order.orderQty && quantity < order.orderQty))
      ) {
        setNotEnoughStocks(true);
      } else {
        newOrder(order);
        setShowFinishModal(true);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromReview, requestStatus]);

  useEffect(() => {
    if (showFinishModal) {
      setShowCreateOrderModal(false);
      setFromReview(false);
    }
  }, [showFinishModal]);

  const handleBuySellClick = (isSellClicked: boolean) => {
    if (checkIfSymbolIsHalted(symbolsStatusCache)) {
      setIsHalted(true);
      return;
    }

    if (isSellClicked && positionType === PositionType.Sell && !isMarketRegularHours(marketState)) {
      setVisibleRegularTradingHoursStartDialog(true);
      return;
    }

    let title = `${isSellClicked ? lang.commonOrderSideButtonSell() : lang.commonOrderSideButtonBuy()}`;
    setIsSell(isSellClicked);
    setShowFinishModal(false);

    if ((!isSellClicked
      && positionType === PositionType.Sell)
      || (isSellClicked && positionType === PositionType.Buy)) {
      setPositionsQuantity(quantity);
    } else {
      setPositionsQuantity(0);
    }
    if (positionType === PositionType.Sell && isSellClicked) {
      title = `${lang.commonShort()} ${title}`;
    }

    if (isMobile) {
      const target = isSellClicked ? PositionType.Sell : PositionType.Buy;
      navigate(`/${target}/${id}`, {
        state: {
          stateSymbol: id,
          stateTarget: target,
          origin: UI_ROUTES.myAccount,
        },
        replace: true,
      });
    } else {
      setModalTitle(title);
      setShowCreateOrderModal(!showCreateOrderModal);
    }
  };

  const onCloseResultOrderModal = () => {
    if (orderInProgress) {
      resetStatus();
    }
    if (!marketClosed) {
      getOpenPositions();
      myStocksUpdate(false);

      if (status === OrderStatusEnum.Filled) {
        getAccSummary();
      }

      if (isOneOf(status, [
        OrderStatusEnum.Filled,
        OrderStatusEnum.PartiallyFilled,
        OrderStatusEnum.Rejected,
        OrderStatusEnum.Expired,
      ])) {
        getUnreadCount();
      }
    }
    SubscribeGroupCache.setComponent('initial');
    getOrders({ tenNStatuses_StringList: PENDING_ORDERS_REQUEST_TENNSTATUS, isPendingOrdersCall: true });
  };

  const handleCloseOrder = (value) => {
    SubscribeGroupCache.setComponent('initial');
    setShowCreateOrderModal(value);
  };

  const handleRenderModalCreateOrder = () => {
    if (modalTitle === lang.commonOrderSideButtonSell()
    || modalTitle === lang.commonOrderSideButtonBuy()
    || ((modalTitle.includes(lang.commonOrderSideButtonSell()) && isMarketRegularHours(marketState)))) {
      return (
        <ModalCreateOrder
          isMyAccount
          symbol={name}
          orderSide={isSell ? OrderSideEnum.Sell : OrderSideEnum.Buy}
          positionsQuantity={positionsQuantity}
          isOpenModalCreateOrder={showCreateOrderModal}
          setOrderInfo={(value) => setOrder(value)}
          handlePopUp={handleCloseOrder}
          setFromReview={(value) => setFromReview(value)}
        />
      );
    }

    return null;
  };

  const handleSelectIcon = () => (
    <img
      src={BuyAndSellDot}
      className={styles.dotIcon}
      alt="img"
    />
  );

  const handleShowButtons = () => {
    if (showButtons === true) {
      return true;
    }

    if (showButtons === false) {
      return false;
    }

    return true;
  };

  const showResultModal = (orderTimeoutPassed || hasNoResponse || rejectedOrder) && showFinishModal;

  useEffect(() => {
    if (showResultModal) {
      getBuyingPowerFromOMS();
    }
  }, [showResultModal]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isMobile ? (
        <CapitalBuySellButtonsConditions noContent>
          <div className={isSwipeable ? styles.wrapperButtonsSwipable : styles.wrapperButtonsMobile}>
            <Button
              className={isSwipeable ? styles.btnSellMobileSwipable : styles.btnSellMobile}
              variant={ButtonTypesEnum.Link}
              onClick={() => handleBuySellClick(true)}
            >
              {lang.commonOrderSideButtonSell()}
            </Button>
            <Button
              className={isSwipeable ? styles.btnBuyMobileSwipable : styles.btnBuyMobile}
              variant={ButtonTypesEnum.Link}
              onClick={() => handleBuySellClick(false)}
            >
              {lang.commonOrderSideButtonBuy()}
            </Button>
          </div>
        </CapitalBuySellButtonsConditions>
      ) : (
        <CapitalBuySellButtonsConditions noContent>
          <div className={styles.wrapperButtons}>
            {handleShowButtons() && (
              <Select
                disableUnderline
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    style: {
                      backgroundColor: '#191C2E',
                      color: '#FFFFFF',
                      borderRadius: 12,
                    },
                  },
                  getContentAnchorEl: null,
                }}
                IconComponent={handleSelectIcon}
                onMouseEnter={() => setIsSellOrBuySelected && setIsSellOrBuySelected(true)}
                onMouseLeave={() => setIsSellOrBuySelected && setIsSellOrBuySelected(false)}
              >
                <ClickableDiv
                  className={styles.actionButton}
                  onClick={() => handleBuySellClick(false)}
                >
                  <img
                    src={ArrowBuyOrSell}
                    alt="buy"
                  />
                  <span className={styles.text}>
                    {lang.commonOrderSideButtonBuy()}
                  </span>
                </ClickableDiv>
                <ClickableDiv
                  className={styles.actionButton}
                  onClick={() => handleBuySellClick(true)}
                >
                  <img
                    src={ArrowBuyOrSell}
                    className={styles.image}
                    alt="sell"
                  />
                  <span className={styles.text}>
                    {lang.commonOrderSideButtonSell()}
                  </span>
                </ClickableDiv>
              </Select>
            )}
          </div>
        </CapitalBuySellButtonsConditions>
      )}
      {isHalted && <HaltedSymbol symbolName={row.companyName ?? name} toggleModal={() => setIsHalted(false)} />}
      {visibleRegularTradingHoursStartDialog
        ? (
          <RegularTradingHoursStartModal
            toggleModal={() => setVisibleRegularTradingHoursStartDialog(false)}
          />
        )
        : handleRenderModalCreateOrder()}
      {showResultModal && (
      <ProcessFinishedModal
        symbol={name}
        callStatus={orderStatusToCallStatus(status as OrderStatusEnum)}
        processFailedMessage={
          processFailMessageManager(rejectedOrder, showExpired, name, ordType!, side!, orderQty!)
        }
        processPartiallyRejectedMessage={partiallyRejectedOrder
          ? proccessPartiallyRejectedMessage(name, ordType!, side!, orderQty!, filledSoFar) : undefined}
        processSuccessMessageTitle={rejectedOrder
          ? '' : proccessSuccessMessageTitle(ordType!, side!, status, showFillOrder)}
        processSuccessMessageBody={rejectedOrder
          ? '' : proccessSuccessMessageBody(name, status, orderQty, ordType, showFillOrder, filledSoFar)}
        defaultMessageTitle={getDefaultMessageTitle(name, side!, ordType!, orderQty!, orderInProgress, true)}
        toggleModal={onCloseResultOrderModal}
      />
      )}
      {notEnoughStocks && (
      <ProcessFinishedModal
        symbol={name}
        processSuccessMessageTitle=""
        callStatus={orderStatusToCallStatus(OrderStatusEnum.Rejected)}
        processFailedMessage={lang.webTradingIncorrectStocks()}
        toggleModal={() => { setNotEnoughStocks(false); myStocksUpdate(false); }}
      />
      )}
    </>
  );
};

const mapStateProps = (state: RootState) => ({
  requestStatus: state.trading.requestStatus,
  submittedOrder: state.trading.order,
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  resetStatus: () => dispatch(resetRequestStatus()),
  newOrder: (order: Order) => dispatch(serviceNewOrder({ order })),
  getOrders: (data: AccountTradeHistoryPayloadData) => dispatch(getAccountTradeHistory(data)),
  getOpenPositions: (symbol?: string) => dispatch(getPositions({ symbol })),
  myStocksUpdate: (hasUpdate: boolean) => dispatch(setHasOrderUpdate(hasUpdate)),
  getAccSummary: () => dispatch(getAccountSummary()),
  getUnreadCount: () => dispatch(getUnreadMessageCount()),
  getBuyingPowerFromOMS: () => dispatch(getBuyingPowerFromOMSCall()),
});

const connector = connect(mapStateProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(ButtonsSellAndBuy);
